'use client'

import Link from 'next/link'
import Image from 'next/image'
import { Headphones, Flame, Moon, Sun, Coffee, Youtube, Dices, Ear } from 'lucide-react'
import { useMetrica } from 'next-yandex-metrica'

// Add type definition for format objects
interface Format {
  id: string
  title: string
  subtext?: string
  description: React.ReactNode
  icon: React.ReactNode
  image: string
  bgColor: string
  youtubeUrl?: string
  rutubeUrl?: string // Optional property
}

// Format data
export const formats: Format[] = [
  {
    id: 'budnya4ok',
    title: 'Буднячок',
    subtext: 'Liquid, Mainstream',
    description:
      'Лёгкий драм-н-бейс, Мелодичный Liquid Funk и бодрый Mainstream в тёплых эфирах будничных дней.',
    icon: <Sun className="w-6 h-6 text-yellow-400" />,
    image: '/assets/img/formats/budnya4ok.png',
    bgColor: 'from-yellow-900/50',
    youtubeUrl: 'https://www.youtube.com/playlist?list=PLONjfPEXt3IfM-rkt9l9DB0tH00ZyTvCL',
    // rutubeUrl: 'https://rutube.ru/playlist/example4',
  },
  {
    id: 'peklo',
    title: 'Пекло',
    subtext: 'Neurofunk',
    description:
      'Когда душа требует лютого жара: грязные биты, бешеная энергия и никакой жалости. Нейрофанк в чистом виде — для тех, кто любит мощный разнос.',
    icon: <Flame className="w-6 h-6 text-red-500" />,
    image: '/assets/img/formats/peklo.png',
    bgColor: 'from-red-900/50',
    youtubeUrl: 'https://www.youtube.com/playlist?list=PLONjfPEXt3Ie1zdvQmFjPMRmoYHecjWTS',
  },
  {
    id: 'chistilishche',
    title: 'Чистилище',
    subtext: 'Deep, Dark Drum&Bass',
    description: (
      <>
        Глубина, темнота и лёгкий налёт мистики. <br /> Эфиры для тех, кто хочет нырнуть в
        атмосферные звуковые лабиринты.
      </>
    ),
    icon: <Headphones className="w-6 h-6 text-blue-400" />,
    image: '/assets/img/formats/chistilishche.png',
    bgColor: 'from-purple-900/50',
    youtubeUrl: 'https://www.youtube.com/playlist?list=PLONjfPEXt3IcMPZvMCuWgJetrO_Tkrw7P',
  },
  {
    id: 'dobrae-utro',
    title: 'ДобрАЕ Утро',
    description: (
      <>
        Утренние стримы, чтобы проснуться, похихикать и подзарядиться настроением на весь день.{' '}
        <br /> Когда хочется послушать тёплый ДнБ под чай или кофе.
      </>
    ),
    icon: <Coffee className="w-6 h-6 text-orange-400" />,
    image: '/assets/img/formats/dobrae-utro.png',
    bgColor: 'from-orange-900/50',
    youtubeUrl: 'https://www.youtube.com/playlist?list=PLONjfPEXt3IfHokPtsCJR8ly2FK01X6F9',
  },
  {
    id: 'pepelishche',
    title: 'Пепелище',
    subtext: 'Darkside',
    description: (
      <>
        Самые мрачные и тяжёлые эфиры. <br /> Когда драм-н-бейс становится саундтреком к
        апокалипсису — всё правильно, это «Пепелище».
      </>
    ),
    icon: <Moon className="w-6 h-6 text-purple-400" />,
    image: '/assets/img/formats/pepelishche.png',
    bgColor: 'from-purple-900/50',
    youtubeUrl: 'https://www.youtube.com/playlist?list=PLONjfPEXt3Icm87mfDVZLjpEsibiBu9oQ',
  },
  {
    id: 'vpervyje-slyshu',
    title: 'Впервые Слышу',
    subtext: 'Drum & Bass',
    description:
      'Шоу, в котором я ставлю девушкам Drum & Bass, а они оценивают его! Главное условие — этот жанр должен быть незнаком гостье передачи.',
    icon: <Ear className="w-6 h-6 text-pink-400" />,
    image: '/assets/img/formats/vpervqje-slqshu.jpg',
    bgColor: 'from-pink-900/50',
    youtubeUrl: 'https://www.youtube.com/playlist?list=PLONjfPEXt3IcRhyYuSkA55h-mHrpCy-MB',
  },
  {
    id: 'ruletka',
    title: 'Рулетка',
    description: (
      <>
        Шоу, где каждый может попытать удачу. <br /> Импровизация, риск и неожиданные треки. Крутите
        драм машину вместе с нами!
      </>
    ),
    icon: <Dices className="w-6 h-6 text-green-400" />,
    image: '/assets/img/formats/ruletka.png',
    bgColor: 'from-green-900/50',
    youtubeUrl: 'https://www.youtube.com/playlist?list=PLONjfPEXt3IeO71UA59EPYW7Ce1dqJdbf',
  },
]

interface FormatListProps {
  activeFormat: number
  onFormatChange: (index: number) => void
}

export default function FormatList({ activeFormat, onFormatChange }: FormatListProps) {
  const { reachGoal } = useMetrica()
  return (
    <div className="space-y-4" role="tablist" aria-labelledby="formats-section-title">
      {formats.map((format, index) => {
        const isActive = activeFormat === index

        return (
          <div
            id={`format-${format.id}-tab`}
            key={index}
            className="relative"
            role="tab"
            aria-selected={isActive}
            aria-labelledby={`format-${format.id}-title`}
            aria-describedby={`format-${format.id}-subtext`}
          >
            <button
              onClick={() => onFormatChange(index)}
              className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                isActive
                  ? 'bg-black/50 border-l-4 border-radio-blue'
                  : 'bg-black/20 border-l-4 border-transparent hover:border-radio-blue/30'
              }`}
            >
              {/* Header row with icon and title */}
              <div className="flex items-center mb-2">
                <div
                  className={`mr-3 p-2 rounded-full ${isActive ? 'bg-black/50' : 'bg-black/30'}`}
                >
                  {format.icon}
                </div>
                <div className="flex items-baseline flex-wrap gap-x-2">
                  <h3
                    className={`text-xl font-menu ${isActive ? 'text-radio-blue' : 'text-gray-200'}`}
                  >
                    {format.title}
                  </h3>
                  {format.subtext && (
                    <span
                      id={`format-${format.id}-subtext`}
                      className="text-sm text-gray-400 font-light"
                    >
                      {format.subtext}
                    </span>
                  )}
                </div>
              </div>

              {/* Description and image - only visible when active */}
              <div
                role="tabpanel"
                aria-labelledby={`format-${format.id}-tab`}
                className={`overflow-hidden transition-all duration-300 ${
                  isActive ? 'max-h-[1000px] opacity-100 mt-2' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="pl-0">
                  <p className="text-gray-300 text-sm lg:text-base">{format.description}</p>

                  {/* Format image - only visible on mobile and tablet when active */}
                  {isActive && (
                    <div className="lg:hidden mt-4 relative rounded-lg overflow-hidden shadow-md">
                      <div className="relative aspect-[1.4] w-full">
                        <Image
                          src={format.image || '/placeholder.svg'}
                          alt={format.title}
                          width={940}
                          height={671}
                          className="object-cover"
                          sizes="calc(100vw - 70px)"
                        />
                        <div
                          className={`absolute inset-0 bg-gradient-to-t ${format.bgColor}`}
                        ></div>
                      </div>

                      {/* Watch Episodes Links */}
                      <div className="absolute bottom-0 left-0 p-3 w-full">
                        <div className="flex flex-wrap gap-2 mt-2">
                          {format.youtubeUrl && (
                            <Link
                              href={format.youtubeUrl}
                              onClick={() => reachGoal(`youtube-link-${format.id}-click`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-red-600/90 hover:bg-red-600 text-white text-xs rounded-md transition-all duration-300 shadow-md hover:shadow-lg hover:shadow-red-600/30 hover:scale-105 hover:brightness-110 transform"
                            >
                              <Youtube className="w-3 h-3 mr-1" />
                              <span>
                                <span className="hidden sm:inline">Смотреть на </span>
                                YouTube
                              </span>
                            </Link>
                          )}

                          {format.rutubeUrl && (
                            <Link
                              href={format.rutubeUrl}
                              onClick={() => reachGoal(`rutube-link-${format.id}-click`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-blue-900/90 hover:bg-blue-900 text-white text-xs rounded-md transition-all duration-300 shadow-md hover:shadow-lg hover:shadow-blue-900/30 hover:scale-105 hover:brightness-110 transform"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-3 h-3 mr-1"
                              >
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-9.5v6l5-3z" />
                              </svg>
                              <span>
                                <span className="hidden sm:inline">Смотреть на </span>
                                RuTube
                              </span>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </button>
          </div>
        )
      })}
    </div>
  )
}

export function FormatDetail({ format }: { format: Format }) {
  const { reachGoal } = useMetrica()
  return (
    <div className="relative h-[300px] sm:h-[350px] md:h-[450px] w-full rounded-lg overflow-hidden shadow-xl shadow-black/20">
      <Image
        src={format.image || '/placeholder.svg'}
        alt={`Постер для ${format.title}`}
        width={560}
        height={450}
        className="object-cover w-full h-full"
      />
      <div
        className={`absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t ${format.bgColor}`}
      ></div>
      <div className="absolute bottom-0 left-0 p-6 w-full">
        {/* Watch Episodes Links */}
        <div className="flex flex-wrap gap-3 mt-4">
          {format.youtubeUrl && (
            <Link
              href={format.youtubeUrl}
              onClick={() => reachGoal(`youtube-link-${format.id}-click`)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Смотреть ${format.title} на YouTube`}
              className="inline-flex items-center px-4 py-2 bg-red-600/90 hover:bg-red-600 text-white text-sm rounded-md transition-all duration-300 shadow-md hover:shadow-lg hover:shadow-red-600/30 hover:scale-105 hover:translate-y-[-2px] hover:brightness-110 transform"
            >
              <Youtube className="w-4 h-4 mr-2 group-hover:animate-pulse" />
              <span>
                <span className="hidden sm:inline">Смотреть на </span>
                YouTube
              </span>
              <span className="absolute inset-0 rounded-md bg-white/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
            </Link>
          )}

          {format.rutubeUrl && (
            <Link
              href={format.rutubeUrl}
              onClick={() => reachGoal(`rutube-link-${format.id}-click`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-900/90 hover:bg-blue-900 text-white text-sm rounded-md transition-all duration-300 shadow-md hover:shadow-lg hover:shadow-blue-900/30 hover:scale-105 hover:translate-y-[-2px] hover:brightness-110 transform"
              aria-label={`Смотреть ${format.title} на RuTube`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4 mr-2"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-9.5v6l5-3z" />
              </svg>
              <span>
                <span className="hidden sm:inline">Смотреть на </span>
                RuTube
              </span>
              <span className="absolute inset-0 rounded-md bg-white/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
